// measurementChain.ts
import { splitValueAndUnit } from './measurementValueParts';
import { bsa as echoBsa } from './echoCalc';

/**
 * Smart Reporting — field chaining (Nikhil, 25 Aug call, priority #1).
 *
 * "If someone updates something in the top header or anywhere, then everything
 * should change according to that." Editing Height or Weight must recompute
 * BSA, and every value indexed to BSA must follow — through as many levels as
 * the formula needs (LVIDd → LV Mass → LVMI is three deep).
 *
 * WHAT THIS IS NOT:
 * - Not a new formula set. Every edge below already exists in production, in
 *   `pages/reports/helper.ts` (`computeDerivedFields`) or `Utils/echoCalc.ts`.
 *   This module only records *which value depends on which* so an edit can be
 *   pushed forward. If a formula and this file ever disagree, the formula in
 *   `helper.ts` is the one that ran at report-creation time and wins.
 * - Not a replacement for the SR mapping. The mapping (`requiredMeasurementsJSON`
 *   → `filterMeasurements` → `computeDerivedFields`) still produces every initial
 *   value exactly as it does today. Chaining only reacts to a human edit made
 *   afterwards, inside the open report.
 *
 * DIRECTION IS ONE-WAY, ALWAYS FORWARD.
 * Editing BSA updates the indexed values below it and leaves Height/Weight
 * alone — the client's actual workflow (25 Aug call). It is a data-integrity
 * hazard, so a mid-chain edit that no longer agrees with its own inputs raises
 * a non-blocking warning (`ChainResult.warnings`) rather than being refused or
 * silently corrected.
 *
 * Values are the report's *display strings* ("4.5 cm", "1.85 m²", "32 mmHg"),
 * because that is what lives in the document. Units below therefore mirror the
 * display units produced by `convertUnits` in helper.ts, NOT the SR's raw units.
 */

/** A value that participates in the chain, keyed by its report data key. */
export type ChainFieldSpec = {
  /** Clinical name, shown in the edit popup and in warnings. */
  label: string;
  /** Unit appended to a recomputed value. Empty for dimensionless ratios. */
  unit: string;
  /** Decimal places, matching `fractionDigitsForMeasurement` in helper.ts. */
  decimals: number;
  /**
   * Keys this value is computed from. A field with no `inputs` is a root
   * (a directly measured quantity) — it is only ever edited, never derived.
   */
  inputs?: string[];
  /**
   * Recompute from parent values (already parsed to numbers in display units).
   * Returns null when the inputs cannot produce a meaningful value; the
   * existing value is then left untouched rather than blanked.
   */
  compute?: (v: Record<string, number | null>) => number | null;
  /**
   * For indexed values whose numerator never prints on the report (LVEDVI,
   * LVESVI, LA Vol Ind — the SR volumes behind them are stripped as `_RAW`
   * before the report is built). They cannot be recomputed from scratch, but
   * they are exactly `numerator / BSA`, so a BSA change rescales them:
   *   new = old × oldBSA / newBSA.
   * Expressed as the key they are indexed to.
   */
  rescaleBy?: string;
  /**
   * No fixed shape and no unit to reattach — the popup hands over the whole text and
   * writes back exactly what the clinician typed, verbatim.
   *
   * `EF_Prose` is the only one today: Nikhil confirmed the wording used in the LV
   * paragraph and Conclusions can legitimately disagree with the table's computed
   * `EF_Biplane` figure (a sonographer's descriptive impression — "45-60%, mildly
   * reduced" — isn't required to match a strict Simpson's Biplane number), so it is a
   * separate identity, synced between its own two occurrences only, never with the
   * table.
   */
  freeform?: boolean;
};

const n = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

/** ΔP = 4v² (simplified Bernoulli), v in m/s — same as echoCalc.bernoulliGradient. */
const bernoulli = (vMs: number | null): number | null => {
  const v = n(vMs);
  return v === null ? null : 4 * v * v;
};

const divide = (a: number | null, b: number | null): number | null => {
  const x = n(a);
  const y = n(b);
  if (x === null || y === null || y === 0) {
    return null;
  }
  return x / y;
};

/**
 * The dependency graph.
 *
 * Only fields with a real, already-implemented formula are edges. Anything
 * needing an input the report does not print (RVOT diameter for Qp:Qs, IVC
 * collapse % for RAP-from-IVC, SR volumes for EF) is deliberately left as a
 * root: chaining a value we cannot honestly recompute would be worse than not
 * chaining it.
 */
export const CHAIN_FIELDS: Record<string, ChainFieldSpec> = {
  // ── roots: measured, editable, never derived ───────────────────────────
  height: { label: 'Height', unit: 'm', decimals: 2 },
  weight: { label: 'Weight', unit: 'kg', decimals: 1 },
  IVSWd: { label: 'IVSd', unit: 'cm', decimals: 1 },
  LVIDd: { label: 'LVIDd', unit: 'cm', decimals: 1 },
  LVIDs: { label: 'LVIDs', unit: 'cm', decimals: 1 },
  LVPWD: { label: 'LVPWd', unit: 'cm', decimals: 1 },
  LVOT_Diam: { label: 'LVOT Diameter', unit: 'cm', decimals: 1 },
  LVOT_VTI: { label: 'LVOT VTI', unit: 'cm', decimals: 1 },
  LVOT_Vmax: { label: 'LVOT Vmax', unit: 'm/s', decimals: 1 },
  AV_VTI: { label: 'AV VTI', unit: 'cm', decimals: 1 },
  AV_Vmax: { label: 'AV Vmax', unit: 'cm/s', decimals: 1 },
  TR_Vmax: { label: 'TR Vmax', unit: 'm/s', decimals: 1 },
  PV_Vmax: { label: 'PV Vmax', unit: 'm/s', decimals: 1 },
  // 2 decimals, mirroring TWO_DECIMAL_FIELDS in pages/reports/helper.ts — that formats on
  // build, this on edit, and a mismatch would re-round the value the report printed.
  E_vel: { label: 'E velocity', unit: 'm/s', decimals: 2 },
  A_vel: { label: 'A velocity', unit: 'm/s', decimals: 2 },
  E_lateral: { label: "E' lateral", unit: 'cm/s', decimals: 2 },
  E_sept: { label: "E' septal", unit: 'cm/s', decimals: 2 },
  PHT: { label: 'Pressure Half-Time', unit: 'ms', decimals: 1 },
  RAP: { label: 'RA Pressure', unit: 'mmHg', decimals: 1 },
  ARD: { label: 'Aortic Root Diameter', unit: 'cm', decimals: 1 },
  Asc_Aorta: { label: 'Ascending Aorta', unit: 'cm', decimals: 1 },
  // EF has no computable inputs here — the SR volumes behind it are stripped as `_RAW`
  // before the report is built — so it is a root. In the graph purely for identity: the
  // measurement TABLE's copy is the one place it has to be a clean number.
  EF_Biplane: { label: 'EF Biplane', unit: '%', decimals: 0 },
  // The LV paragraph and Conclusions copies — deliberately a SEPARATE identity from the
  // table (Nikhil, 17 Sep): a sonographer's wording there ("45-60%, mildly reduced") is
  // allowed to disagree with the table's computed EF_Biplane figure, so it only syncs
  // with itself.
  EF_Prose: { label: 'EF (report text)', unit: '', decimals: 0, freeform: true },

  // ── level 1 ───────────────────────────────────────────────────────────
  bsa: {
    label: 'BSA',
    unit: 'm²',
    decimals: 2,
    inputs: ['height', 'weight'],
    // DuBois (echoCalc.bsa), the same formula `resolveReportBSA` uses, so a chained
    // BSA matches the one the report was born with. Height prints in metres.
    compute: v => {
      const h = n(v.height);
      const w = n(v.weight);
      if (h === null || w === null || h <= 0 || w <= 0) {
        return null;
      }
      return echoBsa(h * 100, w);
    },
  },
  LV_Mass: {
    label: 'LV Mass',
    unit: 'g',
    decimals: 0,
    inputs: ['IVSWd', 'LVIDd', 'LVPWD'],
    // ASE cube formula, identical to computeDerivedFields / echoCalc.lvMassCube.
    compute: v => {
      const ivsd = n(v.IVSWd);
      const lvidd = n(v.LVIDd);
      const lvpwd = n(v.LVPWD);
      if (ivsd === null || lvidd === null || lvpwd === null) {
        return null;
      }
      const sum = ivsd + lvidd + lvpwd;
      return 0.8 * (1.04 * (Math.pow(sum, 3) - Math.pow(lvidd, 3))) + 0.6;
    },
  },
  TR_PG: {
    label: 'TR Peak Gradient',
    unit: 'mmHg',
    decimals: 1,
    inputs: ['TR_Vmax'],
    compute: v => bernoulli(v.TR_Vmax),
  },
  AV_PG: {
    label: 'AV Peak Gradient',
    unit: 'mmHg',
    decimals: 1,
    inputs: ['AV_Vmax'],
    compute: v => bernoulli(v.AV_Vmax),
  },
  PV_PG: {
    label: 'PV Peak Gradient',
    unit: 'mmHg',
    decimals: 1,
    inputs: ['PV_Vmax'],
    compute: v => bernoulli(v.PV_Vmax),
  },
  EA: {
    label: 'E/A ratio',
    unit: '',
    decimals: 1,
    inputs: ['E_vel', 'A_vel'],
    compute: v => divide(v.E_vel, v.A_vel),
  },
  Avg_EE: {
    label: "Average E/E'",
    unit: '',
    decimals: 2,
    inputs: ['E_vel', 'E_lateral', 'E_sept'],
    // E prints in m/s, E' in cm/s — convert E before dividing, or the ratio is
    // out by 100.
    compute: v => {
      const e = n(v.E_vel);
      const lat = n(v.E_lateral);
      const sep = n(v.E_sept);
      if (e === null) {
        return null;
      }
      const annulus = [lat, sep].filter((x): x is number => x !== null);
      if (annulus.length === 0) {
        return null;
      }
      const mean = annulus.reduce((s, x) => s + x, 0) / annulus.length;
      return divide(e * 100, mean);
    },
  },
  MVA_PHT: {
    label: 'MVA (PHT)',
    unit: 'cm²',
    decimals: 1,
    inputs: ['PHT'],
    // 220 / PHT — echoCalc.mvaPressureHalfTime.
    compute: v => divide(220, v.PHT),
  },
  DPI: {
    label: 'DPI',
    unit: '',
    decimals: 2,
    inputs: ['LVOT_VTI', 'AV_VTI'],
    compute: v => divide(v.LVOT_VTI, v.AV_VTI),
  },
  AVA_VTI: {
    label: 'AVA (VTI)',
    unit: 'cm²',
    decimals: 1,
    inputs: ['LVOT_Diam', 'LVOT_VTI', 'AV_VTI'],
    // Continuity equation — echoCalc.avaContinuity.
    compute: v => {
      const d = n(v.LVOT_Diam);
      const lvot = n(v.LVOT_VTI);
      if (d === null || lvot === null) {
        return null;
      }
      const area = Math.PI * Math.pow(d / 2, 2);
      return divide(area * lvot, v.AV_VTI);
    },
  },
  AVA_Vmax: {
    label: 'AVA (Vmax)',
    unit: 'cm²',
    decimals: 1,
    inputs: ['LVOT_Diam', 'LVOT_Vmax', 'AV_Vmax'],
    // Same continuity equation with peak velocities in place of the VTIs.
    compute: v => {
      const d = n(v.LVOT_Diam);
      const lvot = n(v.LVOT_Vmax);
      if (d === null || lvot === null) {
        return null;
      }
      const area = Math.PI * Math.pow(d / 2, 2);
      return divide(area * lvot, v.AV_Vmax);
    },
  },
  RVSP: {
    label: 'RVSP',
    unit: 'mmHg',
    decimals: 0,
    inputs: ['TR_Vmax', 'TR_PG', 'RAP'],
    /**
     * The tricuspid gradient plus the right atrial pressure.
     *
     * TR_PG is preferred over deriving it again from TR_Vmax. When the velocity is on
     * the report the two are identical — TR_PG is computed from it — but a sonographer
     * may enter the GRADIENT and leave the velocity blank, which is how the cart often
     * reports it. Reading only the velocity left RVSP empty with both of its clinical
     * inputs sitting on the report.
     */
    compute: v => {
      const grad = n(v.TR_PG) ?? bernoulli(v.TR_Vmax);
      const rap = n(v.RAP);
      if (grad === null || rap === null) {
        return null;
      }
      return grad + rap;
    },
  },
  ARD_Index: {
    label: 'Aortic Root Index',
    unit: 'cm/m²',
    decimals: 1,
    inputs: ['ARD', 'bsa'],
    compute: v => divide(v.ARD, v.bsa),
  },
  Asc_Aorta_Index: {
    label: 'Ascending Aorta Index',
    unit: 'cm/m²',
    decimals: 1,
    inputs: ['Asc_Aorta', 'bsa'],
    compute: v => divide(v.Asc_Aorta, v.bsa),
  },
  LVEDVI: {
    label: 'LVEDV Index',
    unit: 'ml/m²',
    decimals: 0,
    inputs: ['bsa'],
    rescaleBy: 'bsa',
  },
  LVESVI: {
    label: 'LVESV Index',
    unit: 'ml/m²',
    decimals: 0,
    inputs: ['bsa'],
    rescaleBy: 'bsa',
  },
  LA_Vol_Ind: {
    label: 'LA Volume Index',
    unit: 'ml/m²',
    decimals: 0,
    inputs: ['bsa'],
    rescaleBy: 'bsa',
  },

  // ── level 2: depends on a derived value ───────────────────────────────
  LVMI: {
    label: 'LV Mass Index',
    unit: 'g/m²',
    decimals: 0,
    inputs: ['LV_Mass', 'bsa'],
    compute: v => divide(v.LV_Mass, v.bsa),
  },
};

export const isChainField = (key: string): boolean =>
  Object.prototype.hasOwnProperty.call(CHAIN_FIELDS, key);

/**
 * Leading number of a display string ("4.5 cm" → 4.5, "1.85 m²" → 1.85).
 * Returns null for anything without one, so a field a clinician has replaced
 * with prose ("not measured") stops the chain instead of poisoning it.
 */
export function parseChainValue(display: unknown): number | null {
  if (typeof display === 'number') {
    return Number.isFinite(display) ? display : null;
  }
  if (typeof display !== 'string') {
    return null;
  }
  const match = /-?\d+(?:\.\d+)?/.exec(display.replace(/,/g, ''));
  if (!match) {
    return null;
  }
  const parsed = parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Format a number back into the report's display form for `key`. */
export function formatChainValue(key: string, value: number): string {
  const spec = CHAIN_FIELDS[key];
  if (!spec) {
    return String(value);
  }
  const text = value.toFixed(spec.decimals);
  return spec.unit ? `${text} ${spec.unit}` : text;
}

/** Direct children of `key` — every field naming it as an input. */
export function dependentsOf(key: string): string[] {
  return Object.keys(CHAIN_FIELDS).filter(k => (CHAIN_FIELDS[k].inputs || []).includes(key));
}

/**
 * Every field reachable downstream of `key`, in an order where a field always
 * appears after all of its own inputs. That ordering is what makes an
 * arbitrarily long chain work in a single pass: LVIDd → LV Mass → LVMI
 * recomputes LV Mass before LVMI reads it.
 *
 * Depth-first post-order; the `visiting` set makes a malformed graph terminate
 * instead of recursing forever.
 */
export function downstreamOrder(key: string): string[] {
  const ordered: string[] = [];
  const done = new Set<string>();
  const visiting = new Set<string>();

  const visit = (node: string) => {
    if (done.has(node) || visiting.has(node)) {
      return;
    }
    visiting.add(node);
    dependentsOf(node).forEach(visit);
    visiting.delete(node);
    done.add(node);
    if (node !== key) {
      ordered.push(node);
    }
  };

  visit(key);
  // Post-order yields deepest-first; the chain must run shallowest-first.
  return ordered.reverse();
}

export type ChainWarning = {
  key: string;
  label: string;
  message: string;
};

export type ChainResult = {
  /** key → new display string, for every field that actually changed. */
  updates: Record<string, string>;
  /** Non-blocking data-integrity notes for the human. */
  warnings: ChainWarning[];
};

const RELATIVE_TOLERANCE = 0.01;

/**
 * Recompute everything downstream of an edit.
 *
 * @param editedKey  the field the clinician just changed
 * @param values     current display values, keyed the same way the report data
 *                   object is (pass `collectSmartFieldValues(editor)` merged
 *                   over the report's own data). The edited field's NEW value
 *                   must already be in here.
 * @param previousValues the same map as it stood before the edit. Only needed
 *                   for `rescaleBy` fields (the BSA-indexed values whose
 *                   numerator never prints); omit it and they are left alone.
 *
 * Rules, all deliberate:
 * - Forward only. An input of the edited field is never touched, so editing
 *   BSA leaves Height and Weight exactly as they were.
 * - A dependent whose own inputs are incomplete is SKIPPED, not blanked. A
 *   report that shows a value today keeps showing it.
 * - A recomputed value identical to what is already displayed is not emitted,
 *   so the document is not marked dirty for nothing.
 * - Editing a value that has inputs of its own (a mid-chain edit) produces a
 *   warning when the typed value no longer agrees with those inputs. The edit
 *   still stands — this is the flag Nikhil asked for, not a veto.
 */
export function recomputeChain(
  editedKey: string,
  values: Record<string, unknown>,
  previousValues?: Record<string, unknown>
): ChainResult {
  const updates: Record<string, string> = {};
  const warnings: ChainWarning[] = [];

  if (!isChainField(editedKey)) {
    return { updates, warnings };
  }

  // Working numeric snapshot. Later stages read updated values from here, which
  // is how a multi-level chain propagates in one pass.
  const numeric: Record<string, number | null> = {};
  Object.keys(CHAIN_FIELDS).forEach(key => {
    numeric[key] = parseChainValue(values[key]);
  });

  // State as it was BEFORE this edit. `rescaleBy` fields need the value their
  // parent held beforehand — including when the edit IS that parent (editing
  // BSA directly, where `values` already carries the new BSA), and when the
  // parent is recomputed mid-pass (height → BSA → LA Vol Ind).
  const before: Record<string, number | null> = {};
  Object.keys(CHAIN_FIELDS).forEach(key => {
    before[key] = previousValues
      ? parseChainValue(previousValues[key])
      : numeric[key];
  });

  // Mid-chain edit: does the typed value still agree with its own inputs?
  const editedSpec = CHAIN_FIELDS[editedKey];
  if (editedSpec.compute && editedSpec.inputs?.length) {
    const expected = editedSpec.compute(numeric);
    const actual = numeric[editedKey];
    if (expected !== null && actual !== null) {
      const scale = Math.max(Math.abs(expected), 1e-9);
      if (Math.abs(expected - actual) / scale > RELATIVE_TOLERANCE) {
        warnings.push({
          key: editedKey,
          label: editedSpec.label,
          message:
            `${editedSpec.label} no longer matches ` +
            `${editedSpec.inputs.map(k => CHAIN_FIELDS[k]?.label || k).join(' / ')} ` +
            `(${formatChainValue(editedKey, expected)} expected). ` +
            `Values below it have been updated from the value you entered.`,
        });
      }
    }
  }

  // The edited value itself is part of the write set. A field printed in more than one
  // place — EF is in the measurement table, the Left Ventricle paragraph and the
  // Conclusions — only stays consistent if every occurrence is rewritten, and the caller
  // applies these updates to ALL occurrences of a key. Without this, editing a field with
  // no dependents (EF has none) produced no writes at all and its twin never moved.
  const editedDisplay = values[editedKey];
  if (typeof editedDisplay === 'string' && editedDisplay.trim()) {
    const trimmed = editedDisplay.trim();
    // EF's mark covers the figure only — the "%" is static text left in place beside it
    // (see chainFieldMarkup.ts / chainFieldBackfill.ts). Writing the joined "69 %" back
    // into that mark would plant a second "%" next to the one already sitting there.
    updates[editedKey] =
      editedSpec.unit === '%' ? splitValueAndUnit(trimmed).number || trimmed : trimmed;
  }

  for (const key of downstreamOrder(editedKey)) {
    const spec = CHAIN_FIELDS[key];
    let next: number | null = null;

    if (spec.compute) {
      next = spec.compute(numeric);
    } else if (spec.rescaleBy) {
      // Indexed value with no printed numerator: it scales inversely with the
      // value it is indexed to (numerator = old index × old parent, held
      // constant). No-ops when the parent did not actually move.
      const parentKey = spec.rescaleBy;
      const previousParent = before[parentKey];
      const nextParent = numeric[parentKey];
      const current = numeric[key];
      if (previousParent !== null && nextParent !== null && current !== null && nextParent !== 0) {
        next = (current * previousParent) / nextParent;
      }
    }

    if (next === null || !Number.isFinite(next)) {
      continue; // cannot compute honestly — leave what the mapping produced
    }

    const formatted = formatChainValue(key, next);
    const currentDisplay = typeof values[key] === 'string' ? (values[key] as string).trim() : '';
    numeric[key] = parseChainValue(formatted);

    if (formatted !== currentDisplay) {
      updates[key] = formatted;
    }
  }

  return { updates, warnings };
}
